import React, { useState } from 'react';
import { Button, Form, Input, NavBar, SpinLoading, Switch } from 'antd-mobile';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { profileWhatsAppAccountCreateStyles } from './styles';
import { WhatsAppAuthMode } from '../../../api/whatsapp';
import { BindAccountFormValues, ProfileWhatsAppAccountCreateState, WHATSAPP_PHONE_PATTERN } from './types';

interface ProfileWhatsAppAccountCreateViewProps {
  state: ProfileWhatsAppAccountCreateState;
  onSubmit: (values: BindAccountFormValues) => Promise<void>;
  onConfirmSubmit: () => Promise<void>;
  onCancelConfirm: () => void;
  onSwitchMode: (mode: WhatsAppAuthMode) => Promise<void>;
  onResetDraft: () => Promise<void>;
}

export const ProfileWhatsAppAccountCreateView: React.FC<ProfileWhatsAppAccountCreateViewProps> = ({
  state,
  onSubmit,
  onConfirmSubmit,
  onCancelConfirm,
  onSwitchMode,
  onResetDraft,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation('profile');
  const [setDefaultChecked, setSetDefaultChecked] = useState(false);

  return (
    <div className="webview-page" style={profileWhatsAppAccountCreateStyles.page}>
      <NavBar onBack={() => navigate('/profile/whatsapp-accounts')}>
        {t('waAccounts.bindPageNavTitle')}
      </NavBar>

      <div className="webview-section">
        <div style={profileWhatsAppAccountCreateStyles.card}>
          <div style={profileWhatsAppAccountCreateStyles.title}>{t('waAccounts.bindTitle')}</div>
          <div style={profileWhatsAppAccountCreateStyles.subtitle}>{t('waAccounts.bindSubtitle')}</div>

          {state.attempt ? (
            <div style={profileWhatsAppAccountCreateStyles.connectionCard}>
              <div style={profileWhatsAppAccountCreateStyles.connectionHeader}>
                <div style={profileWhatsAppAccountCreateStyles.connectionTitle}>{t('waAccounts.connectDialogTitleFallback')}</div>
                <Button
                  size="mini"
                  fill="outline"
                  onClick={() => void onSwitchMode(state.authMode === 'pairing_code' ? 'qr' : 'pairing_code')}
                >
                  {state.authMode === 'pairing_code' ? t('waAccounts.connectSwitchToQr') : t('waAccounts.connectSwitchToPairing')}
                </Button>
              </div>
              {state.targetPhoneNumber ? (
                <div style={profileWhatsAppAccountCreateStyles.phoneGuardBox}>
                  <div style={profileWhatsAppAccountCreateStyles.phoneGuardLabel}>{t('waAccounts.bindTargetPhoneLabel')}</div>
                  <div style={profileWhatsAppAccountCreateStyles.phoneGuardPhone}>{state.targetPhoneNumber}</div>
                  <div style={profileWhatsAppAccountCreateStyles.phoneGuardHint}>
                    {t('waAccounts.bindTargetPhoneHint', { phone: state.targetPhoneNumber })}
                  </div>
                </div>
              ) : null}
              {state.errorMessage ? (
                <div style={profileWhatsAppAccountCreateStyles.connectionError}>{state.errorMessage}</div>
              ) : state.attempt.auth_payload.pairing_code ? (
                <>
                  <div style={profileWhatsAppAccountCreateStyles.codeText}>{state.attempt.auth_payload.pairing_code}</div>
                  <div style={profileWhatsAppAccountCreateStyles.connectionHint}>{t('waAccounts.connectPairingHint')}</div>
                </>
              ) : state.qrImage ? (
                <>
                  <img alt="qr" src={state.qrImage} style={profileWhatsAppAccountCreateStyles.qrImage} />
                  <div style={profileWhatsAppAccountCreateStyles.connectionHint}>{t('waAccounts.connectQrHint')}</div>
                </>
              ) : (
                <div style={profileWhatsAppAccountCreateStyles.connectionLoading}>
                  <SpinLoading />
                  <span>{t('waAccounts.connectLoading', { phase: state.attempt.status })}</span>
                </div>
              )}
              <Button
                block
                fill="none"
                disabled={state.submitting}
                style={profileWhatsAppAccountCreateStyles.resetButton}
                onClick={() => void onResetDraft()}
              >
                {t('waAccounts.bindWrongPhoneReset')}
              </Button>
            </div>
          ) : (
          <Form
            layout="vertical"
            onFinish={async (values) => {
              await onSubmit({
                ...values,
                set_default: setDefaultChecked,
              } as BindAccountFormValues);
            }}
          >
            <div style={profileWhatsAppAccountCreateStyles.fieldGroup}>
              <label style={profileWhatsAppAccountCreateStyles.fieldLabel}>
                <span style={profileWhatsAppAccountCreateStyles.requiredMark}>*</span>
                {t('waAccounts.phoneLabel')}
              </label>
              <Form.Item
                name="phone_number"
                rules={[
                  { required: true, message: t('waAccounts.phoneRequired') },
                  {
                    validator: (_, value) => {
                      const normalizedValue = typeof value === 'string' ? value.trim() : '';
                      if (!normalizedValue || WHATSAPP_PHONE_PATTERN.test(normalizedValue)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error(t('waAccounts.phoneFormatRequired')));
                    },
                  },
                ]}
              >
                <div style={profileWhatsAppAccountCreateStyles.inputShell}>
                  <span style={profileWhatsAppAccountCreateStyles.inputIcon}>
                    <PhoneFieldGlyph />
                  </span>
                  <Input placeholder={t('waAccounts.phonePlaceholder')} clearable style={profileWhatsAppAccountCreateStyles.input} />
                </div>
              </Form.Item>

              <label style={profileWhatsAppAccountCreateStyles.fieldLabel}>{t('waAccounts.aliasLabel')}</label>
              <Form.Item name="alias">
                <div style={profileWhatsAppAccountCreateStyles.inputShell}>
                  <span style={profileWhatsAppAccountCreateStyles.inputIcon}>
                    <NoteFieldGlyph />
                  </span>
                  <Input placeholder={t('waAccounts.aliasPlaceholder')} clearable style={profileWhatsAppAccountCreateStyles.input} />
                </div>
              </Form.Item>
            </div>

            <div style={profileWhatsAppAccountCreateStyles.switchCard}>
              <div>
                <div style={profileWhatsAppAccountCreateStyles.switchTitle}>{t('waAccounts.setDefaultLabel')}</div>
                <div style={profileWhatsAppAccountCreateStyles.switchHint}>{t('waAccounts.setDefaultHint')}</div>
              </div>
              <Switch checked={setDefaultChecked} onChange={setSetDefaultChecked} />
            </div>

            <Button block type="submit" loading={state.submitting} style={profileWhatsAppAccountCreateStyles.submitButton}>
              {t('waAccounts.bindAndConnectSubmit')}
            </Button>
          </Form>
          )}
        </div>
      </div>

      {state.pendingConfirm ? (
        <ConfirmPhoneModal
          phone={state.pendingConfirm.phone_number}
          submitting={state.submitting}
          onConfirm={() => void onConfirmSubmit()}
          onCancel={onCancelConfirm}
          texts={{
            title: t('waAccounts.bindPhoneConfirmTitle'),
            description: t('waAccounts.bindPhoneConfirmContent'),
            alert: t('waAccounts.bindPhoneConfirmAlert'),
            confirm: t('waAccounts.bindPhoneConfirmOk'),
            cancel: t('waAccounts.bindPhoneConfirmCancel'),
          }}
        />
      ) : null}
    </div>
  );
};

// ====================== Confirm Phone Modal ======================
// Custom modal that mirrors the design mock: purple phone badge, big phone
// number, red warning, and a two-button action row.
interface ConfirmPhoneModalProps {
  phone: string;
  submitting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  texts: {
    title: string;
    description: string;
    alert: string;
    confirm: string;
    cancel: string;
  };
}

const ConfirmPhoneModal: React.FC<ConfirmPhoneModalProps> = ({ phone, submitting, onConfirm, onCancel, texts }) => {
  return (
    <div style={profileWhatsAppAccountCreateStyles.modalMask} role="dialog" aria-modal="true">
      <div style={profileWhatsAppAccountCreateStyles.modalCard}>
        <div style={profileWhatsAppAccountCreateStyles.modalIconBadge}>
          <PhoneBadgeGlyph />
        </div>
        <div style={profileWhatsAppAccountCreateStyles.modalTitle}>{texts.title}</div>
        <div style={profileWhatsAppAccountCreateStyles.modalPhone}>{phone}</div>
        <div style={profileWhatsAppAccountCreateStyles.modalDescription}>{texts.description}</div>

        <div style={profileWhatsAppAccountCreateStyles.modalAlert}>
          <span style={profileWhatsAppAccountCreateStyles.modalAlertIcon}>
            <AlertCrossGlyph />
          </span>
          <span style={profileWhatsAppAccountCreateStyles.modalAlertText}>{texts.alert}</span>
        </div>

        <div style={profileWhatsAppAccountCreateStyles.modalActions}>
          <Button
            block
            disabled={submitting}
            style={profileWhatsAppAccountCreateStyles.modalCancelButton}
            onClick={onCancel}
          >
            {texts.cancel}
          </Button>
          <Button
            block
            loading={submitting}
            style={profileWhatsAppAccountCreateStyles.modalConfirmButton}
            onClick={onConfirm}
          >
            {texts.confirm}
          </Button>
        </div>
      </div>
    </div>
  );
};

const PhoneBadgeGlyph: React.FC = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7.155 4h2.184c.444 0 .83.302.937.733l.726 2.95a.967.967 0 0 1-.288.978l-1.6 1.605a12.047 12.047 0 0 0 4.62 4.624l1.6-1.6a.967.967 0 0 1 .978-.288l2.95.726c.43.106.733.493.733.937v2.184a1.953 1.953 0 0 1-2.205 1.94A16.21 16.21 0 0 1 5.213 7.205 1.953 1.953 0 0 1 7.155 4Z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const AlertCrossGlyph: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M5.5 5.5l5 5M10.5 5.5l-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const PhoneFieldGlyph: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5.962 3.333h1.821c.37 0 .692.252.78.611l.606 2.46a.833.833 0 0 1-.24.814L7.596 8.55a10.039 10.039 0 0 0 3.854 3.854l1.333-1.333a.833.833 0 0 1 .814-.24l2.46.606a.803.803 0 0 1 .61.78v1.821a1.628 1.628 0 0 1-1.838 1.618A13.508 13.508 0 0 1 4.344 5.171 1.628 1.628 0 0 1 5.962 3.333Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const NoteFieldGlyph: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4.167 3.333h7.44l3.393 3.393v9.107a1 1 0 0 1-1 1H4.167a1 1 0 0 1-1-1V4.333a1 1 0 0 1 1-1Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M11.25 3.333v2.917A1.25 1.25 0 0 0 12.5 7.5h2.917" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
  </svg>
);
